import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Code2,
  Clock,
  Star,
  GitFork,
  Search,
  Play,
  ArrowRight,
  Sparkles,
  Check,
  Trash2,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import {
  machineCodingCatalogIndex,
  TemplateSummary,
} from '../../data/machine-coding-templates/catalogIndex';
import { templateSource } from '../../data/machine-coding-templates/templateLoader';
import { extractFileContents } from '../../utils/machineCoding/fileTree';
import {
  machineCodingProjectService,
  MachineCodingProject,
} from '../../services/machineCodingProjectService';
import PageHeader from '../../components/common/PageHeader';

const difficultyChip = (difficulty: string) => {
  switch (difficulty) {
    case 'easy':
      return 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30';
    case 'intermediate':
      return 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30';
    case 'hard':
      return 'text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30';
    default:
      return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
  }
};

const formatTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

// Cosmetic URL slug derived from a project name (the real templateId stays in
// the route so the loader and the saved-project safety flow are unaffected).
const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

const MachineCoding: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<MachineCodingProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [forkingId, setForkingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<'all' | 'easy' | 'intermediate' | 'hard'>('all');
  const [customTitle, setCustomTitle] = useState('');

  useEffect(() => {
    machineCodingProjectService
      .listMine()
      .then((p) => setProjects(p))
      .catch(() => setProjects([])) // anonymous / not signed in → just show templates
      .finally(() => setLoadingProjects(false));
  }, []);

  const customSummary = machineCodingCatalogIndex.find((t) => t.isCustom) || null;
  const sampleTemplates = machineCodingCatalogIndex.filter((t) => !t.isCustom);

  const filteredTemplates = sampleTemplates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty =
      selectedDifficulty === 'all' || template.difficulty === selectedDifficulty;
    return matchesSearch && matchesDifficulty;
  });

  const openProject = (project: MachineCodingProject) => {
    // The URL segment is a cosmetic slug derived from the project name; the
    // workspace resolves the real template from the project's forkedFrom via
    // the projectId. Fall back to the template id if the name yields no slug.
    const segment = slugify(project.name) || project.forkedFrom;
    navigate(
      `/practice/machine-coding/workspace/${segment}?projectId=${encodeURIComponent(project.id)}`,
    );
  };

  // Single entry point. If you've already forked this template, resume the most
  // recent fork instead of creating duplicates; otherwise create a fresh one.
  // Anonymous users fall back to an ephemeral (unsaved) session.
  const startPracticing = async (template: TemplateSummary, customName?: string) => {
    setActionError(null);

    // For the blank custom workspace, always create a fresh, user-named project
    // so a learner can spin up multiple distinct problems. Sample templates
    // resume the most recent existing fork to avoid duplicates.
    if (!template.isCustom) {
      const existing = projects.find((p) => p.forkedFrom === template.id);
      if (existing) {
        openProject(existing);
        return;
      }
    }

    const projectName = customName?.trim() || `My ${template.name}`;
    setForkingId(template.id);
    try {
      // Fork-on-open requires the full Source_Tree, which lives outside the
      // initial bundle — load it on demand, then derive the File_Contents map.
      const tpl = await templateSource.loadTemplate(template.id);
      const fileContents = extractFileContents(tpl.fileStructure);

      // For a named custom workspace, bake the title into package.json so the
      // chosen name is reflected in the code and persisted for further use.
      if (template.isCustom && customName?.trim()) {
        const slug =
          customName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') ||
          'custom-workspace';
        const pkgKey =
          fileContents['/package.json'] !== undefined
            ? '/package.json'
            : fileContents['package.json'] !== undefined
              ? 'package.json'
              : null;
        if (pkgKey) {
          try {
            const pkg = JSON.parse(fileContents[pkgKey]);
            pkg.name = slug;
            fileContents[pkgKey] = JSON.stringify(pkg, null, 2);
          } catch {
            /* leave package.json untouched if it isn't valid JSON */
          }
        }
      }

      const project = await machineCodingProjectService.create({
        name: projectName,
        description: template.isCustom ? 'Custom workspace' : `Forked from ${template.name}`,
        forkedFrom: template.id,
        fileContents,
      });
      setProjects((prev) => [project, ...prev]);
      openProject(project);
    } catch (e: any) {
      if (e?.status === 401) {
        // Not signed in — start an unsaved session and let them know.
        setActionError('You are not signed in, so this session will not be saved. Sign in to keep your work.');
        navigate(`/practice/machine-coding/workspace/${template.id}`);
      } else {
        setActionError('Could not start your project. Please try again.');
      }
    } finally {
      setForkingId(null);
    }
  };

  const deleteProject = async (id: string) => {
    const prev = projects;
    setProjects((p) => p.filter((x) => x.id !== id)); // optimistic
    try {
      await machineCodingProjectService.remove(id);
    } catch {
      setProjects(prev); // rollback
      setActionError('Could not delete project. Please try again.');
    }
  };

  return (
    <div className="nav-tab-page practice-detail-page machine-coding-catalog-page min-h-screen pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PageHeader
          title="Build real projects in a live IDE"
          description="Fork a template into a real in-browser Node.js environment, write code, run it, and run real tests — a faithful machine-coding round. Your forks are saved to your account."
          icon={Code2}
          iconColor="text-primary-600 dark:text-primary-400"
          gradientColors={{ from: 'from-primary-500', via: 'via-primary-600', to: 'to-primary-700' }}
        />

        {actionError && (
          <div className="mb-6 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
            {actionError}
          </div>
        )}

        {/* Your projects */}
        {(loadingProjects || projects.length > 0) && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <GitFork className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Your projects
              </h2>
              {projects.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  {projects.length} saved
                </span>
              )}
            </div>

            {loadingProjects ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-500 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading your projects…
              </div>
            ) : (
              <div className="saved-projects-grid grid grid-cols-1 md:grid-cols-2 gap-3">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="saved-project-card group rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col overflow-hidden"
                  >
                    <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />
                    <div className="saved-project-card__body p-4 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <h3 className="font-bold text-base text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {project.name}
                          </h3>
                          <p className="saved-project-card__source text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                            <span>From</span> {project.description.replace(/^Forked from\s*/i, '')}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteProject(project.id)}
                          className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                          aria-label="Delete project"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="saved-project-card__actions flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-400 mt-auto pt-3 border-t border-gray-100 dark:border-gray-700">
                        <span className="inline-flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5" /> {project.progress}% complete
                        </span>
                        {project.testResults && (
                          <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                            <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            {project.testResults.passed}/{project.testResults.total} tests
                          </span>
                        )}
                        <span className="saved-project-status text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500">Saved project</span>
                        <button
                          onClick={() => openProject(project)}
                          className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 group-hover:translate-x-0.5 transition-transform"
                        >
                          Continue <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Custom workspace — a distinct, blank starting point */}
        {customSummary && (
          <section className="mb-10">
            <div className="machine-coding-subsection-heading">
              <div>
                <h2>Start a new workspace</h2>
                <p>Begin from a blank Node.js project when you already know what you want to build.</p>
              </div>
            </div>
            <div className="custom-workspace-card group rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />
              <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-primary-600 dark:text-primary-400">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-base text-gray-900 dark:text-white leading-tight">
                        {customSummary.name}
                      </h3>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                        Blank
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl">
                      {customSummary.description}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2 w-full sm:w-auto">
                  <input
                    type="text"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') startPracticing(customSummary, customTitle);
                    }}
                    placeholder="Name your workspace"
                    maxLength={200}
                    className="flex-1 sm:w-56 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 dark:text-white outline-none transition"
                  />
                  <button
                    onClick={() => startPracticing(customSummary, customTitle)}
                    disabled={forkingId === customSummary.id}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-50"
                  >
                    {forkingId === customSummary.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4" />
                    )}
                    Start
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Search + difficulty filter */}
        <div className="template-filters flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search templates…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 dark:text-white outline-none transition"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {(['all', 'easy', 'intermediate', 'hard'] as const).map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDifficulty(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${selectedDifficulty === d
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-700'
                  }`}
              >
                {d === 'all' ? 'All levels' : d}
              </button>
            ))}
          </div>
        </div>

        {/* Templates */}
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Templates{' '}
          <span className="text-sm font-normal text-gray-500 dark:text-gray-500">
            ({filteredTemplates.length})
          </span>
        </h2>

        <div className="template-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="template-card group rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col overflow-hidden"
            >
              {/* Top accent strip */}
              <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />

              <div className="p-5 flex flex-col flex-1">
                {/* Icon + Title */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0 group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors">
                    <Code2 className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                  </div>
                  <div className="min-w-0 flex items-center gap-2">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {template.name}
                    </h3>
                    <span
                      className={`flex-shrink-0 px-2 py-0.5 rounded-md text-[11px] font-medium ${difficultyChip(
                        template.difficulty,
                      )}`}
                    >
                      {template.difficulty}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="template-card__description text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4" title={template.description}>
                  {template.description}
                </p>

                {/* Time — difficulty already appears beside the title */}
                <div className="template-card__time flex items-center justify-between gap-3 mb-4 rounded-lg px-3 py-2">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500">Time limit</span>
                  <strong className="text-xs font-semibold text-gray-800 dark:text-gray-200">{formatTime(template.timeLimit)}</strong>
                </div>

                {/* Technologies — one quiet line instead of a tag cloud */}
                <div className="template-card__technologies mb-4">
                  <span>Technologies</span>
                  <p
                    aria-label={`Technologies: ${template.technologies.join(', ')}`}
                    title={template.technologies.join(' · ')}
                  >
                    {template.technologies.length} technologies
                  </p>
                </div>

                {/* Spacer */}
                <div className="flex-1" />

                {/* Footer */}
                <div className="flex items-center justify-end pt-3 border-t border-gray-100 dark:border-gray-700 mt-auto">
                  <button
                    onClick={() => startPracticing(template)}
                    disabled={forkingId === template.id}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 group-hover:translate-x-0.5 transition-transform disabled:opacity-50"
                  >
                    {forkingId === template.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>Start practicing <ArrowRight className="w-4 h-4 ml-0.5" /></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {
          filteredTemplates.length === 0 && (
            <div className="text-center py-16 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
              <Code2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                No templates found
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Try adjusting your search or level filter.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedDifficulty('all');
                }}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400"
              >
                Clear filters <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )
        }
      </div >
    </div >
  );
};

export default MachineCoding;
